import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { supabase } from '@/integrations/supabase/client';
import { queryKeys } from '@/lib/query-keys';
import { useToast } from '@/hooks/use-toast';

import {
  clearOAuthState,
  storeOAuthState,
  validateOAuthState,
} from './useClickUp.oauth';
import { ClickUpConnection, TokenErrorResponse } from './useClickUp.types';

export function useClickUpGenerateOAuthUrl() {
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (redirectUri: string) => {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const response = await supabase.functions.invoke('clickup-oauth', {
        body: { action: 'generate-state', redirect_uri: redirectUri },
      });

      if (response.error) {
        throw new Error(response.error.message || 'Failed to generate OAuth URL');
      }

      const { state, oauth_url, expires_at } = response.data;

      storeOAuthState({
        state,
        expiresAt: expires_at,
        redirectUri,
      });

      return { oauthUrl: oauth_url, state, expiresAt: expires_at };
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to initiate ClickUp connection',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

export function useClickUpConnection() {
  return useQuery({
    queryKey: queryKeys.clickup.connection,
    queryFn: async () => {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) return null;

      const { data, error } = await supabase
        .from('clickup_connections')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error) {
        console.error('Failed to fetch ClickUp connection:', error);
        return null;
      }

      return data as ClickUpConnection | null;
    },
  });
}

export function useClickUpTokenStatus() {
  const { data: connection, isLoading: connectionLoading } = useClickUpConnection();

  return useQuery({
    queryKey: queryKeys.clickup.tokenStatus,
    queryFn: async () => {
      const response = await supabase.functions.invoke('clickup-oauth', {
        body: { action: 'check-token' },
      });

      if (response.error) {
        return {
          valid: false,
          is_expired: false,
          requires_reauth: true,
          error: response.error.message,
        };
      }

      return response.data as {
        valid: boolean;
        is_expired: boolean;
        requires_reauth: boolean;
        expires_at: string | null;
      };
    },
    enabled: !!connection && !connectionLoading,
    staleTime: 5 * 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
  });
}

export function useClickUpRefreshToken() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async () => {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const response = await supabase.functions.invoke('clickup-oauth', {
        body: { action: 'refresh-token' },
      });

      if (response.error) {
        const errorData = response.data as TokenErrorResponse | undefined;
        if (errorData?.requires_reauth) {
          throw new Error(errorData.error || 'Token refresh failed - please reconnect');
        }
        throw new Error(response.error.message || 'Failed to refresh token');
      }

      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.clickup.connection });
      queryClient.invalidateQueries({ queryKey: queryKeys.clickup.tokenStatus });
      toast({ title: 'Token refreshed successfully' });
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to refresh token',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

export function useClickUpOAuthExchange() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ code, state }: { code: string; state: string }) => {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const clientValidation = validateOAuthState(state);
      if (!clientValidation.valid) {
        throw new Error(clientValidation.error || 'Invalid OAuth state');
      }

      const response = await supabase.functions.invoke('clickup-oauth', {
        body: { action: 'exchange', code, state },
      });

      clearOAuthState();

      if (response.error) {
        const errorCode = response.data?.error_code;
        let errorMessage = response.error.message || 'Failed to connect to ClickUp';

        if (errorCode === 'OAUTH_STATE_MISSING' || errorCode === 'OAUTH_STATE_INVALID') {
          errorMessage = 'Security validation failed. Please try connecting again.';
        } else if (errorCode === 'OAUTH_STATE_EXPIRED') {
          errorMessage = 'Connection request expired. Please try connecting again.';
        } else if (errorCode === 'OAUTH_STATE_REPLAYED') {
          errorMessage =
            'This connection request has already been used. Please try connecting again.';
        } else if (errorCode === 'OAUTH_STATE_USER_MISMATCH') {
          errorMessage = 'Security mismatch detected. Please try connecting again.';
        }

        throw new Error(errorMessage);
      }

      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.clickup.connection });
      toast({ title: 'Connected to ClickUp!' });
    },
    onError: (error: Error) => {
      clearOAuthState();
      toast({
        title: 'Failed to connect to ClickUp',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

export function useClickUpDisconnect() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async () => {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const response = await supabase.functions.invoke('clickup-oauth', {
        body: { action: 'disconnect' },
      });

      if (response.error) {
        throw new Error(response.error.message || 'Failed to disconnect');
      }

      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.clickup.connection });
      toast({ title: 'Disconnected from ClickUp' });
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to disconnect',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}